import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { CaseStrip } from "@/components/CasePreview";
import { SiteFooter } from "@/components/SiteFooter";
import { GuideLinks } from "@/components/GuideLinks";
import { LeadForm } from "@/components/LeadForm";
import { StickyCta } from "@/components/StickyCta";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-theme-anpassung";
const TITLE = "Shopify Theme anpassen lassen | 110 € netto pro Stunde";
const DESCRIPTION =
  "Shopify Theme anpassen lassen: Liquid und Sections, Produktseiten, Varianten, mobile Darstellung und technische Fehler. 110 € netto pro Stunde, Umfang vor Start geklärt.";

const faqs = [
  {
    q: "Was kostet eine Theme-Anpassung?",
    a: "110 € netto pro Stunde. Zum Vergleich: Shopify-Freelancer in Deutschland verlangen branchenüblich 90 bis 140 € pro Stunde, Agenturen 120 bis 180 €. Vor dem Start schätze ich den Aufwand für Ihre Aufgabe ein, damit Sie wissen, worauf Sie sich einlassen. Bei größeren Umbauten vereinbaren wir einen schriftlichen Festpreis. Ein Upgrade Ihrer Kategorie- und Kollektionsseiten mit Conversion-Konzept, Mobile- und Speed-Optimierung und updatesicherer Umsetzung startet ab 2.000 € netto.",
  },
  {
    q: "Arbeiten Sie in meinem vorhandenen Theme?",
    a: "Ja, das ist der Normalfall. Ich arbeite in einer Kopie Ihres Themes, damit der laufende Shop unberührt bleibt, und veröffentliche erst nach Ihrer Freigabe.",
  },
  {
    q: "Bleiben Theme-Updates möglich?",
    a: "Anpassungen werden so umgesetzt, dass sie nachvollziehbar dokumentiert sind. Ob ein Update später sauber durchläuft, hängt vom Theme und vom bisherigen Zustand des Codes ab. Pauschal versprechen lässt sich das nicht.",
  },
  {
    q: "Können Sie auch Fehler beheben, die jemand anderes verursacht hat?",
    a: "Ja. Kaputte Sections, doppelte Skripte, Reste alter Apps und Darstellungsfehler auf dem Handy sind ein großer Teil meiner Arbeit. Ich sage Ihnen vorher, ob Reparatur oder Neuaufbau günstiger ist.",
  },
  {
    q: "Wird der Shop dadurch schneller?",
    a: "Aufräumen im Theme hilft der Ladezeit oft, aber die Ursache liegt manchmal bei Apps, Bildern oder externen Skripten. Ich schaue mir das an und sage Ihnen, was realistisch möglich ist.",
  },
];

export const Route = createFileRoute("/shopify-theme-anpassung")({
  component: ThemePage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Shopify Theme anpassen und Entwicklung",
          description: DESCRIPTION,
          serviceType: "Shopify Theme Anpassung",
          areaServed: "DACH",
          provider: { "@type": "Person", name: "Nicolai Schnepf", url: SITE },
          offers: {
            "@type": "Offer",
            price: "110",
            priceCurrency: "EUR",
            description: "110 € netto pro Stunde, Umfang vor Start geklärt.",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

const scope = [
  {
    title: "Liquid und Sections",
    text: "Neue Sections, angepasste Templates und Einstellungen, die Sie danach selbst im Customizer bedienen können, ohne für jede Kleinigkeit wieder anfragen zu müssen.",
  },
  {
    title: "Produktseiten",
    text: "Aufbau, Reihenfolge, Bilder, Beschreibungstexte, Hinweise zu Versand und Verfügbarkeit. Die Seite, an der die Kaufentscheidung tatsächlich fällt.",
  },
  {
    title: "Varianten und Optionen",
    text: "Größen, Farben, Bundles, Mengenstaffeln oder Konfigurationen sauber abgebildet, inklusive Preisen, Bildern und Verfügbarkeiten je Variante.",
  },
  {
    title: "Mobile Darstellung",
    text: "Abstände, Schriftgrößen, klebende Kaufbuttons und Formulare auf dem Handy geprüft. Nicht im Simulator geraten, sondern am Gerät nachgesehen.",
  },
  {
    title: "Technische Fehler",
    text: "Reste alter Apps, doppelte Skripte, kaputte Weiterleitungen und Darstellungsfehler nach Updates. Erst Ursache finden, dann reparieren.",
  },
  {
    title: "Schnittstellen und Daten",
    text: "Warenwirtschaft, DATEV, Versanddienstleister oder Marktplätze angebunden, Produktdaten und Metafelder sauber übernommen. Vorher prüfe ich, ob eine fertige App die Aufgabe günstiger löst.",
  },
];

function ThemePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <main id="top">
        <section className="veil border-b border-border/60 py-16 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
                Shopify · Anpassungen und Entwicklung
              </p>
              <h1 className="mt-5 text-4xl font-bold leading-[1.05] md:text-6xl">
                Shopify Theme <span className="text-signal">anpassen lassen</span>, sauber
                und nachvollziehbar.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-muted-foreground">
                Ihr Shop steht, aber etwas fehlt oder klemmt: eine neue Section, eine bessere
                Produktseite, Varianten, die richtig funktionieren, oder ein Fehler, den
                niemand findet. Ich arbeite in Ihrem vorhandenen Theme.
              </p>
              <p className="mt-4 text-sm text-muted-foreground">
                110 € netto pro Stunde. Den Umfang klären wir vor dem Start, damit Sie
                wissen, worauf Sie sich einlassen.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#anfrage"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
                >
                  Kundenprojekte ansehen
                </Link>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Unverbindlich · Antwort innerhalb von 48 Stunden
              </p>
            </div>
            <div id="anfrage">
              <LeadForm
                source="lp-theme-anpassung"
                showServices
                defaultService="Anpassungen & Entwicklung"
                headline="Aufgabe schildern"
                sub="Sie erhalten innerhalb von 48 Stunden eine kurze Einschätzung, den empfohlenen nächsten Schritt und, soweit anhand Ihrer Angaben möglich, einen groben Aufwand."
                messagePlaceholder="Welche Aufgabe steht an, welches Theme nutzen Sie?"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Womit ich Ihnen helfe</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {scope.map((s) => (
                <div key={s.title} className="shadow-lift rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Typische Aufgaben und was sie kosten</h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Damit Sie vor der Anfrage eine Größenordnung haben: das sind Erfahrungswerte
              aus echten Projekten. Ihren konkreten Aufwand schätze ich vor dem Start.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                { task: "Neue Section im Customizer", time: "2 bis 4 Stunden", cost: "220 bis 440 €", note: "Etwa ein Text-Bild-Bereich, eine Uebersicht oder ein Banner, den Sie danach selbst pflegen." },
                { task: "Produktseite umbauen", time: "3 bis 6 Stunden", cost: "330 bis 660 €", note: "Reihenfolge, Vertrauenselemente, Versandhinweise und mobile Darstellung so, dass die Kaufentscheidung leichter faellt." },
                { task: "Varianten und Bundles", time: "2 bis 5 Stunden", cost: "220 bis 550 €", note: "Groessen, Farben oder Mengenstaffeln sauber abgebildet, inklusive Preisen und Verfuegbarkeiten." },
                { task: "Schnittstellen und Anbindungen", time: "4 bis 12 Stunden", cost: "440 bis 1.320 €", note: "Warenwirtschaft, DATEV, Versanddienstleister oder Marktplaetze angebunden, vorher geprueft, ob eine fertige App die Aufgabe guenstiger loest." },
                { task: "Datenimport und Metafelder", time: "3 bis 8 Stunden", cost: "330 bis 880 €", note: "Produktdaten, Kunden oder Bestellungen sauber uebernommen, Metafelder fuer Zusatzinfos, die Ihr Shop im Customizer pflegbar macht." },
                { task: "Pagespeed und Aufräumarbeiten", time: "2 bis 6 Stunden", cost: "220 bis 660 €", note: "Reste alter Apps entfernt, Skripte entschlackt, Bilder optimiert. Messbar an Ladezeit, nicht an Versprechen." },
                { task: "App einrichten und prüfen", time: "1 bis 3 Stunden", cost: "110 bis 330 €", note: "Die richtige App ausgewaehlt, sauber eingerichtet und geprueft, dass sie nicht mit Theme oder anderen Apps kollidiert." },
                { task: "Fehlersuche und Reparatur", time: "ab 1 Stunde", cost: "ab 110 €", note: "Kaputte Sections, Reste alter Apps, Darstellungsfehler nach Updates. Erst Ursache, dann Reparatur." },
                { task: "Mobile Feinschliff", time: "2 bis 4 Stunden", cost: "220 bis 440 €", note: "Abstaende, Schriftgroessen, klebende Kaufbuttons und Formulare, geprueft am echten Geraet." },
                { task: "Groesserer Umbau", time: "Festpreis", cost: "nach Umfang", note: "Bei mehrtaegigen Aufgaben vereinbaren wir einen Festpreis, der schriftlich feststeht, bevor gebaut wird. Branchenueblich liegen solche Projekte zwischen 1.500 und 5.000 €." },
              ].map((e) => (
                <div key={e.task} className="shadow-lift rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{e.task}</h3>
                  <p className="text-signal mt-2 text-lg font-bold">{e.cost}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{e.time}</p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{e.note}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Alle Angaben netto. Der verbindliche Aufwand steht vor dem Start fest, abgerechnet
              wird nach tatsaechlicher Arbeit, nie mehr als vereinbart.
            </p>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-8 md:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
                Paket · mehr als nur hübsch
              </p>
              <h2 className="mt-4 text-3xl font-bold md:text-4xl">
                Kategorie-Seiten Upgrade <span className="text-signal">ab 2.000 € netto</span>
              </h2>
              <p className="mt-5 max-w-3xl text-muted-foreground leading-relaxed">
                Die Kategorie- und Kollektionsseiten sind die Stelle, an der Besucher entscheiden,
                ob sie bleiben oder gehen. Ein Upgrade mit echtem Conversion-Konzept, Mobile- und
                Speed-Optimierung und einer Umsetzung, die auch das naechste Template-Update sicher
                uebersteht, startet ab 2.000 € netto.
              </p>
              <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {[
                  { title: "Conversion-Konzept", text: "Reihenfolge, Filter, Sortierung und Vertrauenselemente so, dass Besucher schneller finden, was sie kaufen wollen." },
                  { title: "Mobile optimiert", text: "Abstaende, Schriftgroessen und Kaufpfade am echten Geraet geprueft, nicht im Simulator geraten." },
                  { title: "Speed optimiert", text: "Schlanke Sections und gezielter Bild- und Skripteinsatz, damit die Seiten auch bei langsamen Verbindungen laden." },
                  { title: "Updatesicher", text: "Sauber dokumentiert und in einer Kopie umgesetzt, damit das naechste Theme-Update nicht die Anpassungen zerstoert." },
                ].map((p) => (
                  <div key={p.title} className="rounded-lg border border-border bg-card p-5">
                    <h3 className="text-sm font-semibold">{p.title}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{p.text}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-muted-foreground">
                Der Festpreis steht schriftlich vor dem Start fest. Einzelne Anpassungen bleiben
                weiterhin bei 110 € netto pro Stunde.
              </p>
              <div className="mt-8">
                <a
                  href="#anfrage"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Ehrliche Beratung
            </p>
            <h2 className="mt-4 text-3xl font-bold md:text-4xl">
              Ich sage Ihnen auch, <span className="text-signal">was sich nicht lohnt.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Nicht jede Aufgabe braucht Entwicklung. Wenn eine App fuer 10 € im Monat Ihr
              Problem loest, empfehle ich die App, nicht meine Stunden. Wenn Ihr Theme technisch
              am Ende ist, sage ich Ihnen, ob Reparatur oder Neuaufbau auf zwei Jahre gerechnet
              guenstiger ist. Und wenn eine Anpassung zwar schoen aussieht, aber am Umsatz nichts
              aendert, bekommen Sie das vorher zu hoeren.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Das kostet mich kurzfristig Umsatz und bringt mir langfristig Kunden, die wiederkommen.
              Genau so arbeite ich.
            </p>
          </div>
        </section>

        <CaseStrip
          slugs={["maskabel", "deinlunu"]}
          title="Theme- und Entwicklungsarbeit aus echten Shops"
          intro="Bei MASKABEL ging es um Kabelfinder, Preis- und Mengenlogik und schnelle Eingaben auf dem Handy. Bei Lunu um Produktseite, Bundle-Logik und kürzere mobile Kaufwege."
        />

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">So läuft es ab</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                { no: "01", title: "Aufgabe und Zugang", text: "Sie schildern die Aufgabe, ich sehe mir Theme und Shop an und sage Ihnen den zu erwartenden Aufwand." },
                { no: "02", title: "Umsetzung in der Kopie", text: "Gearbeitet wird in einer Kopie Ihres Themes. Der laufende Shop bleibt unberührt, bis Sie freigeben." },
                { no: "03", title: "Freigabe und Livegang", text: "Sie sehen das Ergebnis, wir gehen es durch, danach veröffentliche ich. Abrechnung nach tatsächlichem Aufwand." },
              ].map((s) => (
                <div key={s.no} className="rounded-lg border border-border bg-card p-6">
                  <span className="text-signal font-display text-3xl font-bold">{s.no}</span>
                  <h3 className="mt-3 text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Häufige Fragen</h2>
            <div className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {faqs.map((f) => (
                <div key={f.q} className="bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Noch kein Shop vorhanden?{" "}
              <Link to="/shopify-shop-erstellen-lassen" className="text-primary underline underline-offset-4">
                Shop erstellen lassen
              </Link>
              . Größere Entwicklungsaufgabe?{" "}
              <Link to="/shopify-entwickler" className="text-primary underline underline-offset-4">
                Shopify Entwickler buchen
              </Link>
              . Geht es Ihnen vor allem um mehr Bestellungen?{" "}
              <Link to="/shopify-conversion-optimierung" className="text-primary underline underline-offset-4">
                Conversion-Optimierung
              </Link>
              .
            </p>
          </div>
        </section>

        <section id="kontakt" className="py-20 pb-28 lg:pb-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">
                Schicken Sie mir <span className="text-signal">Ihre Aufgabe</span>.
              </h2>
              <p className="mt-5 text-muted-foreground">
                Kurze Beschreibung reicht. Sie bekommen innerhalb von 48 Stunden eine
                Einschätzung, den empfohlenen nächsten Schritt und, soweit möglich, einen
                groben Aufwand. Kostenlos und unverbindlich.
              </p>
            </div>
            <LeadForm
              source="lp-theme-anpassung"
              compact={false}
              showServices
              defaultService="Anpassungen & Entwicklung"
              headline="Anpassung einschätzen lassen"
              cta="Kostenlose Einschätzung erhalten"
              messagePlaceholder="Welche Aufgabe steht an, welches Theme nutzen Sie?"
            />
          </div>
        </section>
      </main>

      <StickyCta targetId="anfrage" />
      <GuideLinks
        links={[
        { to: "/blog/shopify-theme-auswaehlen", label: "Shopify Theme auswaehlen", text: "Kostenlos, Premium oder eigenes Theme." },
        { to: "/shopify-app-entwicklung", label: "Shopify App Entwicklung", text: "Wann eine App die bessere Loesung ist." },
        { to: "/ratgeber/shopify-pagespeed-optimieren", label: "Pagespeed verbessern", text: "Theme schlank halten statt Apps stapeln." },
        ]}
      />
      <SiteFooter />
    </div>
  );
}
